<template>
  <IndexView />
  <ViewContainer>
    <section class="p-6">
      <PageTitle :title="`Kartu Stok: ${this.item.code} - ${this.item.name}`" />
      <div class="bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg">
        <div v-if="loading" class="flex justify-center items-center h-32">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
        <div v-else>
          <div class="flex flex-col gap-2 mb-5 w-3/6">
            <label class="font-medium text-dark-600 dark:text-dark-400">Range Tanggal</label>
            <DatePicker v-model="rangeDate" selectionMode="range" :manualInput="false"
              @update:modelValue="fetchStockMovements({ page: 1 })" dateFormat="yy-mm-dd" showIcon class="w-full" />
          </div>
          <div class="flex flex-col gap-2 mb-5 w-3/6">
            <InputSelect v-model="selectedTransactionType" label="Tipe Transaksi" id="filter-tipe-transaksi" :options="optionTransactionType"
                    placeholder="Pilih tipe transaksi" optionLabel="name" optionValue="id" />
          </div>
          <TableComponent>
            <TableHead>
              <TableRow>
                <TableHeadCell>No</TableHeadCell>
                <TableHeadCell>Tanggal</TableHeadCell>
                <TableHeadCell>Transaction Type</TableHeadCell>
                <TableHeadCell>Saldo Awal</TableHeadCell>
                <TableHeadCell>In</TableHeadCell>
                <TableHeadCell>Out</TableHeadCell>
                <TableHeadCell>Saldo Akhir</TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow v-for="(stockMovement, index) in stockMovements" :key="stockMovement.id">
                <TableCell>{{ index + 1 }}</TableCell>
                <TableCell>{{ formatTime(stockMovement.createdAt) }}</TableCell>
                <TableCell v-html="transactionType(stockMovement.transactionable_type)"></TableCell>
                <TableCell>{{ stockMovement.opening_balance }}</TableCell>
                <TableCell>{{ stockMovement.qty_in }}</TableCell>
                <TableCell>{{ stockMovement.qty_out }}</TableCell>
                <TableCell>{{ stockMovement.closing_balance }}</TableCell>
              </TableRow>
            </TableBody>
          </TableComponent>
          <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
  import IndexView from "@/views/IndexView.vue";
  import PageTitle from "@/components/PageTitle.vue";
  import TableComponent from "@/components/table/Table.vue";
  import TableHead from "@/components/table/TableHead.vue";
  import TableBody from "@/components/table/TableBody.vue";
  import TableRow from "@/components/table/TableRow.vue";
  import TableCell from "@/components/table/TableCell.vue";
  import TableHeadCell from "@/components/table/TableHeadCell.vue";
  import { formatTime } from "@/helpers/formatTime";
  import { formatRupiah } from "@/helpers/formatRupiah";
  import { mapGetters } from "vuex";
  import ViewContainer from "@/components/ViewContainer.vue";
  import PaginationButtons from "@/components/PaginationButtons.vue";
import { DatePicker } from "primevue";
import InputSelect from "@/components/form/InputSelect.vue";

  export default {
    name: "StockMovement",
    components: {
      IndexView,
      ViewContainer,
      PageTitle,
      TableComponent,
      TableHead,
      TableHeadCell,
      TableBody,
      TableRow,
      TableCell,
      PaginationButtons,
      DatePicker,
      InputSelect,
    },
    props: {
      id: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        loading: true,
        stockMovements: [],
        currentPage: 1,
        totalPages: 1,
        item: {},
        rangeDate: [],
        searchTimeout: null,
        selectedTransactionType: '',
      };
    },
    computed: {
      ...mapGetters("stock_movements", ["allStockMovements"]),
      optionTransactionType() {
        return [
          { id: null, name: 'Semua' },
          { id: 'scrap', name: 'Scrap' },
          { id: 'sales_return', name: 'Retur Penjualan' },
          { id: 'inbound_items_form', name: 'Refill' },
          { id: 'outbound_items_form', name: 'Barang Keluar' },
          { id: 'sales_order', name: 'Penjualan' },
          { id: 'stock_opname', name: 'Stock Opname' },
        ];
      },
    },
    async created() {
      this.fetchSpecifiedItem();
      this.fetchStockMovements();
    },
    watch: {
      currentPage: {
        immediate: true,
        handler(newVal) {
          this.fetchStockMovements({ page: newVal });
        },
      },
      rangeDate: {
        immediate: true,
        handler(newVal) {
          if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
          }
          // TODO: fix this
          this.searchTimeout = setTimeout(() => {
            if (newVal.length > 1) {
              const start_date = this.formateISODate(newVal[0]);
              const end_date = this.formateISODate(newVal[1]);
              let params = { start_date, end_date, date_column: 'createdAt' };
              if (this.selectedTransactionType && this.selectedTransactionType !== '') {
                params.transactionable_type = this.selectedTransactionType;
              }
              this.fetchStockMovements(params)
            } else {
              this.fetchStockMovements({ transactionable_type: this.selectedTransactionType });
            }
          }, 1500);
        },
      },
      selectedTransactionType: {
        immediate: true,
        handler(newVal) {
          if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
          }

          this.searchTimeout = setTimeout(() => {
            if (this.rangeDate.length > 1) {
              const start_date = this.formateISODate(this.rangeDate[0]);
              const end_date = this.formateISODate(this.rangeDate[1]);
              let params = { start_date, end_date, date_column: 'createdAt' };
              if (newVal && newVal !== '') {
                params.transactionable_type = newVal;
              }
              this.fetchStockMovements(params);
            } else {
              this.fetchStockMovements({ transactionable_type: newVal });
            }
          }, 1500);
        },
      },
    },
    methods: {
      formatTime,
      formatRupiah,
      formateISODate(date) {
        const localDate = new Date(date);
        const offset = localDate.getTimezoneOffset();
        const adjustedDate = new Date(localDate.getTime() - (offset * 60 * 1000));
        return adjustedDate.toISOString().split('T')[0];
      },
      transactionType(type) {
        switch (type) {
          case 'scrap':
            return '<span class="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Scrap</span>';
          case 'sales_return':
            return '<span class="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Retur Penjualan</span>';
          case 'inbound_items_form':
            return '<span class="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Refill</span>';
          case 'outbound_items_form':
            return '<span class="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Barang Keluar</span>';
          case 'sales_order':
            return '<span class="px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200">Penjualan</span>';
          case 'stock_opname':
            return '<span class="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Stock Opname</span>';
          default:
            return '<span class="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">' + type + '</span>';
        }
      },
      async fetchSpecifiedItem() {
        const response = await this.$store.dispatch('items/fetchItemById', this.$route.params.id);
        this.item = response.data;
      },
      async fetchStockMovements(params = {}) {
        if (!params.page) params.page = this.currentPage;
        if (!params.limit) params.limit = 25;
        try {
          const { data, pagination } = await this.$store.dispatch(
            "stock_movements/fetchStockMovements",
            {
              ...params,
              item_id: this.$route.params.id,
            }
          );
          this.stockMovements = data.sort((a, b) => b.id - a.id);
          this.totalPages = pagination.totalPages;
        } catch (error) {
          console.error("Error fetching stock movements:", error);
        } finally {
          this.loading = false;
        }
      },
    },
  };
</script>
